import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Upload, FileText, Loader2, Play, RefreshCw, AlertCircle, ChevronLeft, Undo2, CheckCircle2, Sparkles, Bookmark, Save, X, Download, StopCircle } from "lucide-react";

const SAMPLE = `Pre-K Reading & Phonics — 30 Lesson Prompts
Meet the Letter A — Create a fun Pre-K lesson teaching the letter A, its sound /a/, and simple words that begin with A such as apple, ant, alligator, and astronaut.
Meet the Letter B — Teach the letter B and the /b/ sound using ball, bear, banana, and butterfly.`;

const SUBJECTS = [
  ["reading", "Reading & Phonics"],
  ["vocabulary", "Vocabulary"],
  ["math", "Math"],
  ["writing", "Handwriting / Writing"],
  ["science", "Science"],
  ["social_communication", "Social Communication"],
  ["social_studies", "Social Studies"],
  ["sel", "Social-Emotional Learning"],
  ["life_skills", "Life Skills"],
  ["foreign_language", "Foreign Language"],
];
const GRADES = ["Pre-K", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];

export default function AdminBulkLessonUpload() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [format, setFormat] = useState("paste"); // 'paste' | 'csv'
  const [content, setContent] = useState("");
  const [defaultSubject, setDefaultSubject] = useState("");
  const [defaultGrade, setDefaultGrade] = useState("");
  const [preview, setPreview] = useState(null);
  const [parseBusy, setParseBusy] = useState(false);
  const [generate, setGenerate] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  // Load presets on mount
  useEffect(() => { api.listBulkPresets().then((d) => setPresets(d.presets || [])).catch(() => {}); }, []);

  // If arrived from Recent Jobs list with ?job=<id>, auto-load that job.
  useEffect(() => {
    const qid = searchParams.get("job");
    if (qid && !jobId) setJobId(qid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Give the preset a name first."); return; }
    if (!content.trim()) { toast.error("Nothing to save — paste some content first."); return; }
    try {
      await api.saveBulkPreset({
        name, content, format,
        default_subject: defaultSubject || null,
        default_grade: defaultGrade || null,
        generate,
      });
      const d = await api.listBulkPresets();
      setPresets(d.presets || []);
      setPresetName("");
      toast.success(`Preset "${name}" saved`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadPreset = (p) => {
    setFormat(p.format || "paste");
    setContent(p.content || "");
    setDefaultSubject(p.default_subject || "");
    setDefaultGrade(p.default_grade || "");
    setGenerate(!!p.generate);
    setPreview(null);
    toast.success(`Loaded preset "${p.name}"`);
  };

  const removePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.deleteBulkPreset(p.id);
      setPresets((xs) => xs.filter((x) => x.id !== p.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadFailedCsv = () => {
    if (!job || !job.results) return;
    const failed = job.results.filter((r) => !r.ok);
    if (!failed.length) { toast.info("No failed rows to download."); return; }
    // Merge with original preview rows to get the original prompt/subject/grade.
    const byTitle = new Map((preview?.rows || []).map((r) => [r.title, r]));
    const header = ["subject", "grade", "title", "prompt", "tag", "free", "error"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of failed) {
      const orig = byTitle.get(r.title) || {};
      lines.push([
        orig.subject_id || "", orig.grade || "", r.title || "",
        orig.prompt || "", orig.tag || "Lesson", orig.free ? "true" : "false",
        r.error || "",
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk-upload-failed-${(jobId || "job").slice(0, 8)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const parse = async () => {
    if (!content.trim()) { toast.error("Paste some lesson prompts or upload a CSV first."); return; }
    setParseBusy(true);
    try {
      const p = await api.bulkParseLessons({
        format,
        content,
        default_subject: defaultSubject || null,
        default_grade: defaultGrade || null,
      });
      setPreview(p);
      if (!p.rows.length) toast.error("No rows detected. Check your format.");
      else toast.success(`Parsed ${p.count} rows · ${p.ready} ready · ${p.duplicate} duplicate · ${p.skip} skip`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setParseBusy(false); }
  };

  const run = async () => {
    if (!preview || !preview.ready) { toast.error("Nothing ready to upload."); return; }
    setRunBusy(true);
    try {
      const { job_id } = await api.bulkRunLessons({ rows: preview.rows, generate });
      setJobId(job_id);
      toast.success("Upload started");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunBusy(false); }
  };

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const j = await api.bulkJobStatus(jobId);
        if (cancelled) return;
        setJob(j);
        if (j.status === "done" || j.status === "undone" || j.status === "cancelled") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (j.status === "done") toast.success(`Bulk upload finished · ${j.ok} created · ${j.failed} failed`);
          if (j.status === "cancelled") toast.info(`Bulk upload stopped · ${j.ok} created before stop`);
        }
      } catch { /* transient */ }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId]);

  const [cancelBusy, setCancelBusy] = useState(false);
  const stopJob = async () => {
    if (!jobId) return;
    if (!window.confirm("Stop this bulk upload? Rows already in progress will still finish, but nothing new will start.")) return;
    setCancelBusy(true);
    try {
      await api.bulkJobCancel(jobId);
      toast.success("Stopping… rows already running will finish.");
      const j = await api.bulkJobStatus(jobId);
      setJob(j);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCancelBusy(false); }
  };

  const undo = async () => {
    if (!jobId) return;
    if (!window.confirm("Delete every lesson this batch created? This cannot be undone.")) return;
    try {
      const { deleted } = await api.bulkJobUndo(jobId);
      toast.success(`Deleted ${deleted} lessons`);
      const j = await api.bulkJobStatus(jobId);
      setJob(j);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFormat(f.name.toLowerCase().endsWith(".csv") ? "csv" : "paste");
    setContent(await f.text());
    toast.success(`Loaded ${f.name}`);
  };

  const progressPct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8" data-testid="bulk-upload-page">
      <button onClick={() => nav("/settings")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#6B7280] hover:text-[#0D9488]">
        <ChevronLeft size={16} /> Back to Settings
      </button>

      <div className="rounded-3xl border border-[#E5DECF] bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><Upload size={22} /></div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-extrabold text-[#1F2937]">Bulk Upload Lessons</h1>
            <p className="mt-1 font-body text-sm text-[#6B7280]">Paste a list of prompts or upload a CSV. Every row becomes a global lesson owned by you.</p>
          </div>
        </div>

        {/* Prompt Library — presets saved by name for one-click reruns */}
        {presets.length > 0 && (
          <div className="mt-4 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-3" data-testid="preset-library">
            <div className="flex items-center gap-2 text-xs font-bold text-[#6B7280]">
              <Bookmark size={13} className="text-[#7A3FF2]" /> Prompt Library
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-[#E5DECF] px-3 py-1 text-xs font-bold text-[#1F2937]" data-testid={`preset-chip-${p.id}`}>
                  <button onClick={() => loadPreset(p)} className="hover:text-[#0D9488]">{p.name}</button>
                  <button onClick={() => removePreset(p)} aria-label="Delete preset" className="ml-1 text-[#94A3B8] hover:text-[#E05A47]">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Format tabs */}
        <div className="mt-5 flex gap-2">
          <button
            onClick={() => setFormat("paste")}
            data-testid="fmt-paste"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${format === "paste" ? "bg-[#0D9488] text-white" : "bg-[#F2ECE1] text-[#6B7280] hover:bg-[#E5DECF]"}`}
          >
            <FileText size={14} /> Paste text
          </button>
          <button
            onClick={() => setFormat("csv")}
            data-testid="fmt-csv"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${format === "csv" ? "bg-[#0D9488] text-white" : "bg-[#F2ECE1] text-[#6B7280] hover:bg-[#E5DECF]"}`}
          >
            <Upload size={14} /> Upload CSV
          </button>
        </div>

        {format === "paste" && (
          <>
            <p className="mt-4 font-body text-sm text-[#6B7280]">
              Paste your prompt list. Section headers like <span className="font-mono text-[#1F2937]">Pre-K Reading &amp; Phonics — 30 Lesson Prompts</span> or the shorter <span className="font-mono text-[#1F2937]">Kindergarten - Science Lessons</span> set the grade + subject for every row that follows. Each row can be <span className="font-mono text-[#1F2937]">Title — Prompt</span> <strong>or just the prompt on its own line</strong> — when AI-generate is on, we'll write the title for you.
            </p>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="bulk-paste"
              rows={12}
              placeholder={SAMPLE}
              className="mt-2 w-full resize-y rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] p-4 font-mono text-sm text-[#1F2937] outline-none focus:border-[#0D9488]"
            />
          </>
        )}

        {format === "csv" && (
          <>
            <p className="mt-4 font-body text-sm text-[#6B7280]">
              Upload a CSV with columns: <span className="font-mono text-[#1F2937]">subject, grade, title, prompt, tag, free</span>. Subject can be a name (<span className="font-mono">Reading &amp; Phonics</span>) or id (<span className="font-mono">reading</span>).
            </p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} data-testid="bulk-csv-file" className="mt-2 block w-full text-sm text-[#6B7280] file:mr-3 file:rounded-full file:border-0 file:bg-[#0D9488] file:px-4 file:py-2 file:font-bold file:text-white hover:file:bg-[#0B7F74]" />
            {content && (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                data-testid="bulk-csv-preview"
                className="mt-3 w-full resize-y rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] p-4 font-mono text-xs text-[#1F2937] outline-none focus:border-[#0D9488]"
              />
            )}
          </>
        )}

        {/* Defaults for unheadered paste rows */}
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <label className="text-sm font-bold text-[#1F2937]">Fallback subject
            <select value={defaultSubject} onChange={(e) => setDefaultSubject(e.target.value)} data-testid="bulk-default-subject"
              className="mt-1 w-full rounded-xl border border-[#E5DECF] bg-white px-3 py-2 text-sm font-semibold text-[#1F2937]">
              <option value="">Auto-detect / from header</option>
              {SUBJECTS.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </select>
          </label>
          <label className="text-sm font-bold text-[#1F2937]">Fallback grade
            <select value={defaultGrade} onChange={(e) => setDefaultGrade(e.target.value)} data-testid="bulk-default-grade"
              className="mt-1 w-full rounded-xl border border-[#E5DECF] bg-white px-3 py-2 text-sm font-semibold text-[#1F2937]">
              <option value="">Auto-detect / from header</option>
              {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label className="flex items-end gap-2 text-sm font-bold text-[#1F2937]">
            <input type="checkbox" checked={generate} onChange={(e) => setGenerate(e.target.checked)} data-testid="bulk-generate" className="h-5 w-5 rounded border-[#E5DECF] text-[#0D9488]" />
            <span className="inline-flex items-center gap-1"><Sparkles size={13} className="text-[#7A3FF2]" /> Generate tutorial with AI</span>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={parse} disabled={parseBusy}
            data-testid="bulk-parse-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-4 py-2 text-sm font-bold text-[#0B7F74] hover:bg-[#17BEBB]/10 disabled:opacity-50"
          >
            {parseBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Parse / dry-run
          </button>
          <button
            onClick={run}
            disabled={!preview || !preview.ready || runBusy || (jobId && job?.status === "running")}
            data-testid="bulk-run-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF9E1B] px-5 py-2 text-sm font-bold text-[#1F2937] hover:brightness-95 disabled:opacity-50"
          >
            {runBusy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {generate ? "Upload & generate all" : "Upload prompt stubs"}
          </button>
        </div>

        {/* Save as named preset */}
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-[#E5DECF] bg-white/60 p-3">
          <Bookmark size={14} className="text-[#7A3FF2]" />
          <span className="font-body text-xs font-bold text-[#6B7280]">Save this paste as a preset</span>
          <input
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder='e.g. "Pre-K Alphabet 26"'
            data-testid="preset-name"
            className="flex-1 min-w-[160px] rounded-xl border border-[#E5DECF] bg-white px-3 py-1.5 text-sm outline-none focus:border-[#7A3FF2]"
          />
          <button
            onClick={savePreset}
            disabled={!content.trim() || !presetName.trim()}
            data-testid="preset-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#652BDB] disabled:opacity-50"
          >
            <Save size={12} /> Save preset
          </button>
        </div>
      </div>

      {/* Preview table */}
      {preview && (
        <div className="mt-6 overflow-hidden rounded-3xl border border-[#E5DECF] bg-white" data-testid="bulk-preview">
          <div className="flex items-center justify-between border-b border-[#E5DECF] px-5 py-3">
            <div className="font-heading text-lg font-bold text-[#1F2937]">Preview ({preview.count} rows)</div>
            <div className="text-xs font-bold text-[#6B7280]">
              <span className="mr-3 text-[#047857]">{preview.ready} ready</span>
              <span className="mr-3 text-[#B45309]">{preview.duplicate} duplicate</span>
              <span className="text-[#B23125]">{preview.skip} skip</span>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full min-w-[720px] text-left font-body text-sm">
              <thead className="sticky top-0 bg-[#FAF7F2] text-xs font-bold uppercase tracking-widest text-[#6B7280]">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Grade · Subject</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Prompt</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#F2ECE1]">
                    <td className="px-4 py-2 text-[#94A3B8]">{i + 1}</td>
                    <td className="px-4 py-2 font-semibold text-[#1F2937]">{r.grade} · {r.subject_id}</td>
                    <td className="px-4 py-2 font-semibold text-[#1F2937]">
                      {r.title
                        ? r.title
                        : (generate
                            ? <span data-testid={`ai-title-badge-${i}`} className="inline-flex items-center gap-1 rounded-full bg-[#7A3FF2]/10 px-2 py-0.5 text-[11px] font-bold text-[#7A3FF2]" title="No title in the paste — the AI will write one during generation.">
                                <Sparkles size={11} /> AI will name this
                              </span>
                            : <span className="text-[#B23125]">(missing)</span>)}
                    </td>
                    <td className="px-4 py-2 text-[#6B7280]">{(r.prompt || "").slice(0, 90)}{(r.prompt || "").length > 90 ? "…" : ""}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                        r.status === "ready" ? "bg-[#10B981]/15 text-[#047857]" :
                        r.status === "duplicate" ? "bg-[#F59E0B]/15 text-[#B45309]" :
                        "bg-[#E05A47]/15 text-[#B23125]"
                      }`}>{r.status}</span>
                      {r.warnings?.length > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-[#B23125]">
                          <AlertCircle size={11} /> {r.warnings.join(" · ")}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Job progress */}
      {job && (
        <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-5" data-testid="bulk-job-progress">
          <div className="flex items-center justify-between gap-3">
            <div className="font-heading text-lg font-bold text-[#1F2937]">
              {job.status === "running"   && <><Loader2 size={16} className="mr-1 inline animate-spin text-[#0D9488]" /> Building lessons…</>}
              {job.status === "done"      && <><CheckCircle2 size={16} className="mr-1 inline text-[#047857]" /> Bulk upload complete</>}
              {job.status === "cancelled" && <><StopCircle size={16} className="mr-1 inline text-[#B23125]" /> Bulk upload stopped</>}
              {job.status === "undone"    && <>Batch undone</>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm font-bold text-[#6B7280]">
                {job.processed}/{job.total} · <span className="text-[#047857]">{job.ok} ok</span> · <span className="text-[#B23125]">{job.failed} failed</span>
              </div>
              {job.status === "running" && (
                <button
                  onClick={stopJob}
                  disabled={cancelBusy}
                  data-testid="bulk-stop-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-3 py-1.5 text-xs font-bold text-white hover:brightness-95 disabled:opacity-60"
                  title="Stop this bulk upload. Rows in progress will finish."
                >
                  {cancelBusy ? <Loader2 size={12} className="animate-spin" /> : <StopCircle size={12} />}
                  Stop upload
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-[#F2ECE1]">
            <div className={`h-full transition-all ${job.status === "cancelled" ? "bg-[#B23125]" : "bg-[#0D9488]"}`} style={{ width: `${progressPct}%` }} />
          </div>
          {job.status === "cancelled" && (
            <p className="mt-2 text-xs font-semibold text-[#B23125]" data-testid="bulk-cancelled-note">
              Stopped by admin. {job.ok} lessons were saved before the stop; the remaining {Math.max(0, (job.total || 0) - (job.processed || 0))} were skipped. You can re-run just the missing rows from the same paste.
            </p>
          )}
          {(job.status === "done" || job.status === "undone" || job.status === "cancelled") && (
            <div className="mt-4 flex flex-wrap gap-2">
              {(job.status === "done" || job.status === "cancelled") && job.ok > 0 && (
                <button onClick={undo} data-testid="bulk-undo-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47]/10 px-3 py-1.5 text-xs font-bold text-[#B23125] hover:bg-[#E05A47]/20">
                  <Undo2 size={13} /> Undo this batch (delete {job.ok} lessons)
                </button>
              )}
              {job.failed > 0 && (
                <button onClick={downloadFailedCsv} data-testid="bulk-failed-csv" className="inline-flex items-center gap-1.5 rounded-full bg-[#F59E0B]/15 px-3 py-1.5 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/25">
                  <Download size={13} /> Download failed rows as CSV ({job.failed})
                </button>
              )}
            </div>
          )}
          {job.results && job.results.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-bold text-[#6B7280]">Detailed results</summary>
              <ul className="mt-2 max-h-[240px] space-y-1 overflow-y-auto text-xs">
                {job.results.map((r, i) => (
                  <li key={i} className={r.ok ? "text-[#047857]" : "text-[#B23125]"}>
                    {r.ok ? "✓" : "✗"} {r.title || "(unnamed)"}{r.error ? ` — ${r.error}` : ""}
                    {r.ok && r.lesson_id && (
                      <Link to={`/lessons?lesson=${r.lesson_id}`} className="ml-2 text-[#0D9488] hover:underline">open</Link>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
